import React, { useEffect, useState } from 'react';
import { IHistoryStock, useGetClosePricesQuery } from '../../../generated/graphql';
import moment from 'moment';
import { numberWithCommas } from '../../../utils/mathUtils';
import { exportDataAsExcel } from '../../../utils/fileUtiles';
import { Box } from '@mui/material';
import InnerLoader from '../../Common/InnerLoader';
import DenseTable from '../../Common/Tables/DenseTable';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../../Common/Fields/EpraDatePicker';
import { IHeadCell } from '../../Common/Tables/OverviewTable';

interface IProps {
  stockId: string;
  currencyCode: string | undefined;
  stockName: string;
}

const ClosePriceTab: React.FunctionComponent<IProps> = ({
  stockId,
  currencyCode,
  stockName
}: IProps) => {
  const [allData, setAllData] = useState<IHistoryStock[]>([]);
  const [fileteredData, setFileteredData] = useState<IHistoryStock[]>([]);
  const [date, setDate] = useState<Date | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const { data, loading } = useGetClosePricesQuery({
    variables: {
      stock_id: parseInt(stockId)
    },
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (data && data.result) {
      const parsedData = data.result as IHistoryStock[];
      setAllData(parsedData);
      setFileteredData(parsedData);
    }
  }, [data]);

  useEffect(() => {
    if (allData.length > 0) {
      let filteredByDate = allData;
      const currentStartDate = startDate ? new Date(startDate) : null;
      const currentEndDate = endDate ? new Date(endDate) : null;
      const cleanStartDate = currentStartDate
        ? new Date(
            currentStartDate.getFullYear(),
            currentStartDate.getMonth(),
            currentStartDate.getDate(),
            0o0,
            0o0,
            0o0
          ).getTime()
        : null;
      const cleanEndDate = currentEndDate
        ? new Date(
            currentEndDate.getFullYear(),
            currentEndDate.getMonth(),
            currentEndDate.getDate() + 1,
            0o0,
            0o0,
            0o0
          ).getTime()
        : null;
      if (currentStartDate && currentEndDate) {
        if (currentStartDate > currentEndDate) {
          filteredByDate = allData.filter(
            (e) => new Date(e.date).getTime() >= (cleanStartDate as number)
          );
        } else {
          filteredByDate = allData.filter((e) => {
            const scheduledDate = new Date(parseInt(e.date)).getTime();
            return (
              scheduledDate >= (cleanStartDate as number) &&
              scheduledDate < (cleanEndDate as number)
            );
          });
        }
      } else if (currentEndDate && !currentStartDate) {
        filteredByDate = allData.filter(
          (e) => new Date(parseInt(e.date)).getTime() < (cleanEndDate as number)
        );
      } else if (currentStartDate && !currentEndDate) {
        filteredByDate = allData.filter(
          (e) => new Date(parseInt(e.date)).getTime() >= (cleanStartDate as number)
        );
      }
      setFileteredData(filteredByDate);
    }
  }, [startDate, endDate]);

  const exportCurrentClosePrices = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const fileName = `${stockName.replaceAll(' ', '-')}-close-prices-${
      date ? `${moment(date).format('YYYY_MM_DD')}` : `${moment(new Date()).format('YYYY_MM_DD')}`
    }`;
    const csvData = fileteredData.map((d) => ({
      'Date Day': renderDate(d.date),
      Currency: currencyCode,
      'Close Price': d.close_price ? numberWithCommas(d.close_price) : null,
      'Full Mkt Cap': d.engl_mkt_cap ? numberWithCommas(d.engl_mkt_cap) : null,
      'Free Float Market Cap': d.free_float_market_cap
        ? numberWithCommas(d.free_float_market_cap)
        : null,
      'Epra Shares': d.epra_shares ? numberWithCommas(d.epra_shares) : null,
      'NAV Epra': d.nav_epra,
      'NAV Ifrs': d.nav_ifrs,
      NRA: d.nra,
      NTA: d.nta,
      NDV: d.ndv,
      Average: d.average,
      'Premium Discount': d.price_discount,
      'Div Gross': d.div_gross,
      'Gross Div Yield': d.gross_div_yield,
      'Div Net': d.div_net
    }));
    exportDataAsExcel(csvData, fileName);
  };

  const renderDate = (input: string) => {
    return moment(parseInt(input)).format('DD-MM-yy');
  };

  const sortDateColumn = (a: IHistoryStock, b: IHistoryStock) => {
    const first = new Date(parseInt(a.date));
    const second = new Date(parseInt(b.date));
    return first.getTime() - second.getTime();
  };

  const columns = [
    {
      title: 'Date Day',
      field: 'date',
      width: 97,
      type: 'date',
      render: (rowData: IHistoryStock) => renderDate(rowData.date),
      customSort: (a: IHistoryStock, b: IHistoryStock) => sortDateColumn(a, b)
    },
    {
      title: 'Currency',
      width: 77,
      render: () => currencyCode,
      sorting: false
    },
    {
      title: 'Close Price',
      width: 90,
      render: (rowData: IHistoryStock) =>
        rowData.close_price ? numberWithCommas(rowData.close_price) : null,
      type: 'numeric',
      sorting: false
    },
    {
      title: 'Full Mkt Cap',
      width: 101,
      render: (rowData: IHistoryStock) =>
        rowData.engl_mkt_cap ? numberWithCommas(rowData.engl_mkt_cap) : null,
      type: 'numeric',
      sorting: false
    },
    {
      title: 'Free Float Market Cap',
      width: 156,
      field: 'div_net',
      render: (rowData: IHistoryStock) =>
        rowData.free_float_market_cap ? numberWithCommas(rowData.free_float_market_cap) : null,
      sorting: false
    },
    {
      title: 'Epra Shares',
      width: 94,
      render: (rowData: IHistoryStock) =>
        rowData.epra_shares ? numberWithCommas(rowData.epra_shares) : null,
      type: 'numeric',
      sorting: false
    },
    {
      title: 'NAV Epra',
      width: 78,
      field: 'nav_epra',
      type: 'numeric',
      sorting: false
    },
    {
      title: 'NAV Ifrs',
      width: 69,
      field: 'nav_ifrs',
      type: 'numeric',
      sorting: false
    },
    {
      title: 'NRA',
      width: 69,
      field: 'nra',
      type: 'numeric',
      sorting: false
    },
    {
      title: 'NTA',
      width: 46,
      field: 'nta',
      type: 'numeric',
      sorting: false
    },
    {
      title: 'NDV',
      width: 48,
      field: 'ndv',
      type: 'numeric',
      sorting: false
    },
    {
      title: 'Average',
      width: 70,
      field: 'average',
      type: 'numeric',
      sorting: false
    },
    {
      title: 'Premium Discount',
      width: 110,
      field: 'price_discount',
      type: 'numeric',
      sorting: false
    },
    {
      title: 'Div Gross',
      width: 79,
      field: 'div_gross',
      type: 'numeric',
      sorting: false
    },
    {
      title: 'Gross Div Yield',
      width: 134,
      field: 'gross_div_yield',
      type: 'numeric',
      sorting: false
    },
    {
      title: 'Div Net',
      width: 87,
      field: 'div_net',
      type: 'numeric',
      sorting: false
    }
  ] as IHeadCell[];

  return (
    <Box>
      {loading ? (
        <InnerLoader />
      ) : (
        <Box className="print\:w-full" sx={{ pt: '25px' }}>
          <Box sx={{ display: 'flex', position: 'absolute', pl: '25px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box>
                <EpraDatePicker
                  id="close-prices-start-date-picker"
                  label="Start Date"
                  value={startDate}
                  clearable={true}
                  onChange={(date) => setStartDate(date)}
                  minDate={new Date(1980, 0, 1)}
                />
              </Box>
              <Box sx={{ pl: '25px' }}>
                <EpraDatePicker
                  id="close-prices-end-date-picker"
                  label="End Date"
                  value={endDate}
                  clearable={true}
                  onChange={(date) => setEndDate(date)}
                  minDate={startDate || new Date(1980, 0, 1)}
                />
              </Box>
            </LocalizationProvider>
          </Box>
          <DenseTable
            columns={columns}
            data={fileteredData || []}
            handleDetails={() => true}
            handleRemove={() => true}
            exportButtonProps={{
              id: 'export-close-prices-button',
              onClick: exportCurrentClosePrices
            }}
            maxHeight={1050}
            pageSize={15}
          />
        </Box>
      )}
    </Box>
  );
};

export default ClosePriceTab;
